import { t } from '@hefe/i18n';
import {
  BasketballGame,
  EventReportEventableTypeEnum,
  EventReportGroupableType,
  EventReportGroupableTypeEnum,
  EventReportType,
} from '@hefe/models';
import { EventReportsApi, EventReportsSlice, ModalsSlice } from '@hefe/store';
import { Skeleton } from 'antd';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store/store';
import CustomForm from '../../../../core/components/form/custom-form';

export const GENERATE_EVENT_REPORT_WIZARD_PREVIEW_FORM_ID =
  'generate-event-report-wizard-preview-form';

interface GenerateEventReportWizardPreviewFormComponentProps {
  gameId: number;
  game: BasketballGame;
  eventType: EventReportEventableTypeEnum;
  eventId: number;
  groupType?: EventReportGroupableTypeEnum;
  groupId: number;
  setButtonLoading: (loading: boolean) => void;
}

export default function GenerateEventReportWizardPreviewFormComponent(
  props: GenerateEventReportWizardPreviewFormComponentProps
) {
  const { gameId } = props;

  const [
    generatePregameReport,
    { data: responsePreGame, isLoading: isLoadingPreGame },
  ] = EventReportsApi.useGenerateBasketballPreGameReportMutation();

  const [generateReport, { data: responseGame, isLoading: isLoadingGame }] =
    EventReportsApi.useGenerateBasketballGameReportMutation();

  const [createReport] = EventReportsApi.useCreateReportMutation();

  const dispatch = useAppDispatch();
  const wizard = useSelector(EventReportsSlice.generateWizardSelector);

  useEffect(() => {
    if (wizard?.data?.type === EventReportType.PRE) {
      generatePregameReport({
        game_id: gameId,
        writing_type: wizard.data.writing_type ?? 'neutral',
        additional_info: wizard.data.additional_info,
      });
    } else {
      generateReport({
        game_id: gameId,
        quarters: wizard.data.quarters,
        score_home: wizard?.data?.score_home ?? 0,
        score_guest: wizard?.data?.score_guest ?? 0,
        top_performer_home: wizard?.data?.top_performer_home ?? '',
        top_performer_guest: wizard?.data?.top_performer_guest ?? '',
        summary: wizard?.data?.summary ?? '',
        writing_type: wizard?.data?.writing_type ?? 'neutral',
      });
    }
  }, []);

  const onSubmit = (values: any, resetFields?: () => void) => {
    const style = wizard?.data?.writing_type;
    const groupable_type =
      style === 'neutral'
        ? EventReportGroupableType.CLUB
        : EventReportGroupableType.BASKETBALL_TEAM;

    let groupable_id = props.groupId;
    if (props?.game?.team_details) {
      for (const td of props.game.team_details) {
        if (td?.team?.name === style) {
          groupable_id = td.team.id;
          break;
        }
      }
    }

    props.setButtonLoading(true);

    createReport({
      eventable_id: props.eventId,
      eventable_type: props.eventType,
      groupable_id: groupable_id,
      groupable_type: groupable_type,
      text: values.text,
      title: values.title,
      type: wizard?.data?.type,
      visibility: wizard?.data?.visibility,
    })
      .unwrap()
      .then(() => {
        dispatch(EventReportsSlice.actions.resetWizard());
        dispatch(ModalsSlice.modalsActions.closeModal());
      })
      .finally(() => {
        props.setButtonLoading(false);
      });
  };

  const initialTitle = useMemo(() => {
    if (responsePreGame) {
      return responsePreGame.title;
    } else if (responseGame) {
      return responseGame.title;
    }
    return '';
  }, [responsePreGame, responseGame]);

  const initialText = useMemo(() => {
    if (responsePreGame) {
      return responsePreGame.text;
    } else if (responseGame) {
      return responseGame.text;
    }
    return '';
  }, [responsePreGame, responseGame]);

  return (
    <Skeleton loading={isLoadingPreGame || isLoadingGame} active>
      <CustomForm
        fields={[
          {
            label: t('game_reports.generate.wizard.steps.preview.report_title'),
            type: 'text',
            name: 'title',
            initalValue: initialTitle,
            rules: [
              {
                required: true,
                message: t('form_errors.text'),
              },
            ],
          },
          {
            label: '',
            type: 'texteditor',
            name: 'text',
            initalValue: initialText,
            rules: [
              {
                required: true,
                message: t('form_errors.text'),
              },
            ],
          },
        ]}
        onSubmit={onSubmit}
        formId={GENERATE_EVENT_REPORT_WIZARD_PREVIEW_FORM_ID}
      />
    </Skeleton>
  );
}
